import { useTranslation } from 'react-i18next';
import { OverviewTable } from 'app/components/editor/subtoolbar/tags/Overview/OverviewTable/OverviewTable';
import { OverviewHeader } from 'app/components/editor/subtoolbar/tags/Overview/OverviewHeader/OverviewHeader';
import { useTagsByDocumentPaginated, useDocumentTaxonomyConfig, TagWithMetadata, PaginatedTagsParams } from 'entities/tag';
import { FilterGroup, InitialFilter, TagFilters } from 'app/components/editor/sidebar/tags/TagsFilter';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTaggingNavigate, useTaggingSearch } from 'app/router/params';
import { formatTaxonomyName } from 'app/utils/tag-utils';
import Pagination from 'shared/data-display/Pagination';
import { TagLanguageDropdown } from 'app/components/editor/sidebar/tags/TagLanguageDropdown';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

interface OverviewProps {
  documentId: string;
  onTagDetailsClick: (tag: TagWithMetadata) => void;
}

export const Overview = ({ documentId, onTagDetailsClick }: OverviewProps) => {
  const { t } = useTranslation(['all', 'tag', 'taxonomy']);

  const superId = documentId ? parseInt(documentId, 10) : undefined;
  const navigate = useTaggingNavigate();
  const { tagFilter } = useTaggingSearch();

  const initialFilter = useRef<InitialFilter | null>(tagFilter ?? null).current;

  useMemo(() => {
    if (tagFilter) {
      navigate({ replace: true, search: (prev) => ({ ...prev, tagFilter: undefined }) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server-side pagination & filter state
  const [currentPage, setCurrentPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<TagFilters>(() => {
    const base: TagFilters = { status: [], validated: [], type: [], taxonomy: [] };
    if (initialFilter) {
      base[initialFilter.type] = [initialFilter.value];
    }
    return base;
  });

  // Debounce search input
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSetSearchInput = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(0);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  // Build query params from filter state
  const queryParams: PaginatedTagsParams = useMemo(() => {
    const params: PaginatedTagsParams = {
      page: currentPage,
      pageSize: PAGE_SIZE,
      include: ['metadata', 'concept', 'taxonomy', 'unit', 'context'],
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.status.length) params.status = filters.status;
    if (filters.validated.length) params.validationStatus = filters.validated;
    if (filters.type.length) params.type = filters.type;
    if (filters.taxonomy.length) params.taxonomy = filters.taxonomy;
    if (filters.currency?.length) params.currency = filters.currency;
    if (filters.context?.length) params.contextId = filters.context;
    return params;
  }, [currentPage, debouncedSearch, filters]);

  const { data } = useTagsByDocumentPaginated(superId, queryParams);
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(superId);

  const rows = data?.rows ?? [];
  const totalCount = data?.count ?? 0;
  const serverStats = useMemo(() => data?.stats ?? { total: 0, approved: 0, draft: 0, needsAttention: 0, invalid: 0, mandatory: 0 }, [data?.stats]);
  const totalMandatoryTags = data?.totalMandatoryTags ?? 0;

  const taxonomyFilterOptions = useMemo(() => {
    if (!taxonomyConfig?.taxonomySelections?.length) return [];
    return taxonomyConfig.taxonomySelections.map((ts) => ({
      label: ts.taxonomy?.name ? formatTaxonomyName(ts.taxonomy.name) : ts.taxonomyId,
      value: ts.taxonomy?.name || ts.taxonomyId,
    }));
  }, [taxonomyConfig?.taxonomySelections]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    const groups: FilterGroup[] = [
      {
        label: t('tag:taxonomy.plural'),
        value: 'taxonomy',
        icon: <i className="fa-solid fa-layer-group" />,
        options: taxonomyFilterOptions,
      },
      {
        label: t('tag:highlighted.option.status'),
        value: 'status',
        icon: <i className="fa-solid fa-spinner" />,
        options: [
          { label: t('tag:status.approved'), value: 'approved' },
          { label: t('tag:status.draft'), value: 'draft' },
          { label: t('tag:status.need_attention'), value: 'need_attention' },
          { label: t('tag:status.verified'), value: 'verified' },
        ],
      },
      {
        label: t('tag:validation.label'),
        value: 'validated',
        icon: <i className="fa-solid fa-flag" />,
        options: [
          { label: t('tag:status.invalid'), value: 'invalid' },
          { label: t('tag:status.unvalidated'), value: 'unvalidated' },
          { label: t('tag:status.valid'), value: 'valid' },
        ],
      },
      {
        label: t('tag:type'),
        value: 'type',
        icon: <i className="fa-solid fa-tags" />,
        options: [
          { label: t('tag:type.numeric'), value: 'nonFraction' },
          { label: t('tag:type.textBlock'), value: 'nonNumeric' },
        ],
      },
    ];

    if (initialFilter) {
      const hasGroup = groups.some((g) => g.value === initialFilter.type);
      if (!hasGroup) {
        groups.push({
          label: initialFilter.type === 'currency' ? t('tag:monetary.label') : t('tag:date.label'),
          value: initialFilter.type,
          options: [{ label: initialFilter.label, value: initialFilter.value }],
        });
      }
    }

    return groups;
  }, [taxonomyFilterOptions, initialFilter, t]);

  const onAddFilter = useCallback((group: string, value: string) => {
    setFilters((prev) => {
      const prevGroup = prev[group] || [];
      if (prevGroup.includes(value)) {
        return { ...prev, [group]: prevGroup.filter((item) => item !== value) };
      } else {
        return { ...prev, [group]: [...prevGroup, value] };
      }
    });
    setCurrentPage(0);
  }, []);

  const onRemoveFilter = useCallback((group: string, value: string) => {
    setFilters((prev) => {
      const prevGroup = prev[group] || [];
      return { ...prev, [group]: prevGroup.filter((item) => item !== value) };
    });
    setCurrentPage(0);
  }, []);

  const tagCategories = [
    t('tag:name'),
    t('tag:type'),
    t('taxonomy:title'),
    t('tag:highlighted.option.status'),
    t('tag:validation.label'),
    t('tag:mandatory.label'),
    t('tag:ai.origin.label'),
    t('all:tags.page'),
  ];

  const totalTags = serverStats.total;

  const stats = useMemo(
    () => [
      { label: t('tag:total.tags'), value: serverStats.total, type: 'total' },
      { label: t('tag:mandatory.label'), value: serverStats.mandatory, type: 'mandatory' },
      { label: t('tag:status.approved'), value: serverStats.approved, type: 'approved' },
      { label: t('tag:status.draft'), value: serverStats.draft, type: 'draft' },
      { label: t('tag:status.need_attention'), value: serverStats.needsAttention, type: 'need_attention' },
      { label: t('tag:status.invalid'), value: serverStats.invalid, type: 'invalid' },
    ],
    [serverStats, t]
  );

  const paginationPage = useMemo(
    () => ({
      number: currentPage,
      totalPages: Math.ceil(totalCount / PAGE_SIZE),
      totalElements: totalCount,
      size: PAGE_SIZE,
    }),
    [currentPage, totalCount]
  );

  return (
    <div>
      <OverviewHeader
        stats={stats}
        totalTags={totalTags}
        totalMandatoryTags={totalMandatoryTags}
        searchInput={searchInput}
        setSearchInput={handleSetSearchInput}
        filters={filters}
        onAddFilter={onAddFilter}
        onRemoveFilter={onRemoveFilter}
        filterGroups={filterGroups}
        controls={<TagLanguageDropdown documentSuperId={superId} />}
      />

      <OverviewTable categories={tagCategories} tableData={rows} chains={data?.chains} onTagDetailsClick={onTagDetailsClick} pageSize={PAGE_SIZE} />

      <div className="d-flex justify-content-center mt-3">
        <Pagination onPaginate={(page: number) => setCurrentPage(page)} page={paginationPage} pageSize={PAGE_SIZE} />
      </div>
    </div>
  );
};
